export type AdminGoogleSignInPreflightResult =
  | { kind: "ready" }
  | {
      kind: "navigate";
      reason: "canonical-host" | "stale-bundle";
      url: string;
    }
  | {
      kind: "blocked";
      message: string;
    };

type AdminGoogleSignInPreflightInput = {
  authDomain: string;
  currentBundleFingerprint: string;
  currentUrl: string;
  fetchIndexHtml?: (url: string) => Promise<string>;
};

const PREFLIGHT_FAILURE_MESSAGE = "Admin sign-in runtime preflight failed.";
const REFRESH_QUERY_KEY = "__petlog_auth_refresh";

export function extractViteEntryBundleFingerprint(html: string): string | undefined {
  const scriptSources = [...html.matchAll(/<script\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/giu)].map(
    (match) => match[1],
  );

  for (const source of scriptSources) {
    try {
      const pathname = new URL(source, "https://petlog.invalid").pathname;
      const fileName = pathname.split("/").pop();
      if (fileName && /^index-[A-Za-z0-9_-]+\.js$/u.test(fileName)) {
        return fileName;
      }
    } catch {
      // Ignore malformed script sources and continue looking for the Vite entry.
    }
  }

  return undefined;
}

async function fetchCanonicalIndexHtml(url: string): Promise<string> {
  const response = await fetch(url, {
    cache: "no-store",
    headers: {
      accept: "text/html",
    },
  });

  if (!response.ok) {
    throw new Error(`Admin canonical index returned ${response.status}.`);
  }

  return response.text();
}

export async function resolveAdminGoogleSignInPreflight(
  input: AdminGoogleSignInPreflightInput,
): Promise<AdminGoogleSignInPreflightResult> {
  const authDomain = input.authDomain.trim().toLowerCase();

  try {
    const currentUrl = new URL(input.currentUrl);
    const currentHostname = currentUrl.hostname.toLowerCase();
    if (!currentHostname.endsWith(".pages.dev")) {
      return authDomain.endsWith(".pages.dev")
        ? { kind: "blocked", message: PREFLIGHT_FAILURE_MESSAGE }
        : { kind: "ready" };
    }

    const canonicalHostname = resolveCanonicalPagesHostname(currentHostname);
    if (currentHostname !== canonicalHostname) {
      currentUrl.protocol = "https:";
      currentUrl.hostname = canonicalHostname;
      currentUrl.port = "";
      return {
        kind: "navigate",
        reason: "canonical-host",
        url: currentUrl.toString(),
      };
    }

    const canonicalIndexUrl = `https://${canonicalHostname}/`;
    const html = await (input.fetchIndexHtml ?? fetchCanonicalIndexHtml)(canonicalIndexUrl);
    const liveBundleFingerprint = extractViteEntryBundleFingerprint(html);
    if (!liveBundleFingerprint) {
      return {
        kind: "blocked",
        message: PREFLIGHT_FAILURE_MESSAGE,
      };
    }

    if (liveBundleFingerprint !== input.currentBundleFingerprint) {
      currentUrl.searchParams.set(REFRESH_QUERY_KEY, liveBundleFingerprint);
      return {
        kind: "navigate",
        reason: "stale-bundle",
        url: currentUrl.toString(),
      };
    }

    if (authDomain !== canonicalHostname) {
      return {
        kind: "blocked",
        message: PREFLIGHT_FAILURE_MESSAGE,
      };
    }

    return { kind: "ready" };
  } catch {
    return {
      kind: "blocked",
      message: PREFLIGHT_FAILURE_MESSAGE,
    };
  }
}

function resolveCanonicalPagesHostname(hostname: string): string {
  const suffix = ".pages.dev";
  const pagesProjectLabels = hostname.slice(0, -suffix.length).split(".").filter(Boolean);
  const projectName = pagesProjectLabels.at(-1);
  if (!projectName) {
    throw new Error("Unable to resolve the canonical Cloudflare Pages hostname.");
  }
  return `${projectName}${suffix}`;
}
